import React, { forwardRef } from 'react';

import { type VariantProps, tv } from 'tailwind-variants';

import type { LucideProps } from 'lucide-react';

const inputVariants = tv({
  slots: {
    // purposely setting ring-0 to support legacy v2 styles
    // when upgrading to v3, use the unstyled variant instead
    base: 'flex items-center border input-border !ring-0 box-border',
    icon: 'text-gray-90 pointer-events-none',
    input: 'w-full p-0 border-none bg-transparent',
    prefix: 'flex items-center text-gray-90',
    suffix: 'flex items-center text-gray-90',
  },
  variants: {
    size: {
      default: {
        base: 'px-2 py-[5px] rounded-sm',
        input: 'text-inherit',
        prefix: 'pr-1',
        suffix: 'pl-1',
      },
      medium: {
        base: 'px-3 py-2 rounded-sm',
        input: 'text-base',
        prefix: 'pr-1',
        suffix: 'pl-1',
      },
      large: {
        base: 'px-4 py-[9px] rounded-lg text-lg leading-6',
        input: '',
        prefix: 'pr-2',
        suffix: 'pl-2',
      },
    },
    withStartIcon: {
      true: '',
    },
    isDisabled: {
      true: {
        base: 'bg-gray-40',
        input: 'bg-gray-40',
      },
    },
  },
  compoundVariants: [
    {
      size: 'default',
      class: {
        icon: 'size-3.5 pr-1',
      },
    },
    {
      size: 'medium',
      class: {
        icon: 'size-3.5 pr-1',
      },
    },
    {
      size: 'large',
      class: {
        icon: 'size-4 pr-2',
      },
    },
  ],
  compoundSlots: [
    {
      slots: ['base', 'input', 'prefix', 'suffix'],
      size: 'default',
      class: 'text-sm leading-4.5',
    },
    {
      slots: ['base', 'input', 'prefix', 'suffix'],
      size: 'large',
      class: 'text-lg leading-6',
    },
  ],
  defaultVariants: {
    size: 'default',
  },
});

type TextInputV2Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> &
  VariantProps<typeof inputVariants> & {
    onChange?: (value: string) => void;
    className?: string;
    startIcon?: React.ComponentType<LucideProps>;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
  };

const TextInputV2 = forwardRef<HTMLInputElement, TextInputV2Props>(
  (
    { onChange, startIcon: StartIcon, className, type = 'text', size, prefix, suffix, ...props },
    ref,
  ) => {
    const styles = inputVariants({
      size,
      isDisabled: props.disabled,
    });

    return (
      <div className={styles.base({ className })}>
        {StartIcon ? <StartIcon className={styles.icon()} /> : null}
        {prefix && <div className={styles.prefix()}>{prefix}</div>}
        <input
          className={styles.input()}
          onChange={(e) => onChange?.(e.target.value)}
          ref={ref}
          type={type}
          {...props}
        />
        {suffix && <div className={styles.suffix()}>{suffix}</div>}
      </div>
    );
  },
);

export default TextInputV2;
